import { baseApi } from '@store/baseApi';
import {
  type CreateRuleRequest,
  enhancedApi,
  type GetV2AdminRulesApiArg,
  type GetV2AdminRulesSearchApiArg,
  type ProviderRuleListResponse,
  type ProviderRuleResponse2,
  type ProviderRuleSearchResponse,
  type RuleResponse,
  type UpdateRuleRequest,
} from '@store/generated/rules';

/**
 * Feature-specific tag types for admin rules
 */
const ADMIN_RULES_TAG_TYPES = ['AdminRule', 'AdminRuleList', 'AdminRuleSearch'] as const;

/**
 * Admin Rules API - Manually injected endpoints for Admin Rules API
 * Uses /v2/admin/rules routes with organization context support
 */
export const adminRulesApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ADMIN_RULES_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      // List Admin Rules - returns same structure as provider rules
      getAdminRules: builder.query<ProviderRuleListResponse, GetV2AdminRulesApiArg>({
        query: params => ({
          url: '/v2/admin/rules',
          params: {
            page: params.page,
            per_page: params.perPage,
            organization_id: params.organizationId,
            scope: params.scope,
            rule_type: params.ruleType,
          },
        }),
        providesTags: (result, _error, arg) =>
          result
            ? [
                ...result.items.map(item => ({
                  type: 'AdminRule' as const,
                  id: item.rule_id,
                })),
                { type: 'AdminRuleList' as const, id: arg.organizationId ?? 'GLOBAL' },
              ]
            : [{ type: 'AdminRuleList', id: arg.organizationId ?? 'GLOBAL' }],
      }),

      // Get Single Admin Rule - returns same structure as provider rules
      getAdminRuleById: builder.query<ProviderRuleResponse2, string>({
        query: ruleId => `/v2/admin/rules/${ruleId}`,
        providesTags: (_result, _error, ruleId) => [{ type: 'AdminRule', id: ruleId }],
      }),

      // Search Admin Rules (GET query - can be cached and invalidated)
      searchAdminRules: builder.query<ProviderRuleSearchResponse, GetV2AdminRulesSearchApiArg>({
        query: params => ({
          url: '/v2/admin/rules/search',
          params: {
            q: params.q,
            top_k: params.topK,
            organization_id: params.organizationId,
            rule_type: params.ruleType,
          },
        }),
        providesTags: (_result, _error, arg) => [
          { type: 'AdminRuleSearch', id: arg.organizationId ?? 'GLOBAL' },
        ],
      }),

      // Create Admin Rule
      createAdminRule: builder.mutation<RuleResponse, CreateRuleRequest>({
        query: body => ({
          url: '/v2/admin/rules',
          method: 'POST',
          body,
        }),
        invalidatesTags: [
          { type: 'AdminRuleList', id: 'GLOBAL' },
          { type: 'AdminRuleSearch', id: 'GLOBAL' },
        ],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Manually invalidate the list to ensure refetch
            dispatch(
              adminRulesApi.util.invalidateTags([
                { type: 'AdminRuleList', id: 'GLOBAL' },
                { type: 'AdminRuleSearch', id: 'GLOBAL' },
              ]),
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      }),

      // Update Admin Rule
      updateAdminRule: builder.mutation<RuleResponse, { ruleId: string; body: UpdateRuleRequest }>({
        query: ({ ruleId, body }) => ({
          url: `/v2/admin/rules/${ruleId}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_result, _error, { ruleId }) => [{ type: 'AdminRule', id: ruleId }],
      }),

      // Delete Admin Rule
      deleteAdminRule: builder.mutation<unknown, string>({
        query: ruleId => ({
          url: `/v2/admin/rules/${ruleId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, ruleId) => [
          { type: 'AdminRule', id: ruleId },
          { type: 'AdminRuleList', id: 'GLOBAL' },
          { type: 'AdminRuleSearch', id: 'GLOBAL' },
        ],
        async onQueryStarted(ruleId, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              adminRulesApi.util.invalidateTags([
                { type: 'AdminRule', id: ruleId },
                { type: 'AdminRuleList', id: 'GLOBAL' },
                { type: 'AdminRuleSearch', id: 'GLOBAL' },
              ]),
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      }),
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names
export const {
  useGetAdminRulesQuery,
  useGetAdminRuleByIdQuery: useGetAdminRuleQuery,
  useSearchAdminRulesQuery,
  useLazySearchAdminRulesQuery,
  useLazyGetAdminRulesQuery,
  useLazyGetAdminRuleByIdQuery: useLazyGetAdminRuleQuery,
  useCreateAdminRuleMutation,
  useUpdateAdminRuleMutation,
  useDeleteAdminRuleMutation,
} = adminRulesApi;

// Export admin-specific rule management hooks from generated API
export const {
  usePostV2AdminRulesByRuleIdDisableMutation: useDisableAdminRuleMutation,
  usePostV2AdminRulesByRuleIdEnableMutation: useEnableAdminRuleMutation,
  usePostV2AdminRulesByRuleIdForkMutation: useForkAdminRuleMutation,
  usePostV2AdminRulesByRuleIdRevertMutation: useRevertAdminRuleMutation,
  useGetV2AdminRulesByRuleIdRevisionsQuery: useGetAdminRuleRevisionsQuery,
} = enhancedApi;

